import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { ringLabel } from '@theme/r69-ring-sizes';

/*
 * ROTAS69 "Shop the look" — a styled set over one editorial shot, with numbered
 * pins on the image and a buyable row per piece (port of FAORIA `faoria-shop-the-look`,
 * rebuilt for Horizon 4.1.1 + ROTAS69).
 *
 * What is deliberately DIFFERENT from FAORIA:
 *   - FAORIA's metal swatches are GONE. They flipped a `Color` product option that
 *     ROTAS69 products simply do not have: the catalogue is single-variant or carries
 *     Izmērs / Zodiaka Zīme / Burts / Akcentu krāsa, and the metal is baked into the
 *     product itself. Metal-themed SETS are looks of their own instead.
 *   - Several looks live in one section and the shopper switches between them; which
 *     one opens first is chosen before first paint by the section's inline script
 *     (see `data-r69stl-mode`), so a random look costs no flash and survives edge cache.
 *   - Cart adds go through Horizon's CartLinesUpdateEvent deferred promise, so the
 *     native cart drawer opens and refreshes exactly like the PDP form does.
 *   - Ring sizes are relabelled to Ø through the shared @theme/r69-ring-sizes module,
 *     the same single source the main picker and the pairing rail already use.
 *
 * Everything is server-rendered; this file only reacts. State is carried on the unique
 * class `r69stl-current` — never `is-open`/`is-active`, which would land on a theme
 * `:has()` rule and buy a ~32ms document-wide style recalc on every switch.
 */

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

class R69ShopTheLook extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.moneyFormat = this.dataset.moneyFormat;
    this.currency = this.dataset.currency;
    this.i18n = this.#readJSON('[data-r69stl-i18n]');

    this.looks = Array.from(this.querySelectorAll('[data-r69stl-look]'));
    this.tabs = Array.from(this.querySelectorAll('[data-r69stl-tab]'));
    this.tabrail = this.querySelector('[data-r69stl-tabrail]');
    this.cartSet = new Set();

    this.looks.forEach((look) => this.#initLook(look));
    this.#wireTabs();

    // The active look was already chosen before paint by the section's inline script;
    // only its pieces still need the cart state, which costs a request.
    const start = () => this.#refreshCart();
    if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 3000 });
    else setTimeout(start, 600);

    document.addEventListener(StandardEvents.cartLinesUpdate, (e) => this.#onCartEvent(e));
    document.addEventListener('visibilitychange', () => { if (!document.hidden) this.#refreshCart(); });
    window.addEventListener('pageshow', (e) => { if (e.persisted) this.#refreshCart(); });
  }

  #readJSON(sel) {
    const el = this.querySelector(sel);
    if (!el) return {};
    try { return JSON.parse(el.textContent.trim()); } catch (e) { return {}; }
  }

  /* ---------- Looks ---------- */

  #initLook(look) {
    look._pieces = Array.from(look.querySelectorAll('[data-r69stl-piece]'));
    look._pieces.forEach((piece) => {
      const raw = piece.querySelector('[data-r69stl-matrix]');
      let m = { vars: [] };
      try { m = JSON.parse(raw.textContent.trim()); } catch (e) { m = { vars: [] }; }
      piece._m = m;
      piece._sel = piece.querySelector('[data-r69stl-opt]');

      // Ring sizes are stored as the raw option value and shown as the inner Ø, the same
      // label the main picker prints. Done once here, never in a hot path.
      if (piece._sel && m.ring) {
        Array.from(piece._sel.options).forEach((opt) => {
          opt.textContent = ringLabel(opt.value);
        });
      }
      if (piece._sel) piece._sel.addEventListener('change', () => { this.#syncPiece(piece); this.#recalc(look); });

      const add = piece.querySelector('[data-r69stl-add]');
      if (add) {
        add.addEventListener('click', (e) => {
          e.preventDefault();
          const v = this.#variantOf(piece);
          if (v && v.a && !this.cartSet.has(String(v.id))) this.#cartAdd([{ id: v.id, quantity: 1 }], add);
        });
      }
    });

    const addAll = look.querySelector('[data-r69stl-addall]');
    if (addAll) {
      addAll.addEventListener('click', (e) => {
        e.preventDefault();
        const items = look._pieces
          .filter((p) => this.#addable(p))
          .map((p) => ({ id: this.#variantOf(p).id, quantity: 1 }));
        if (items.length) this.#cartAdd(items, addAll);
      });
    }

    // Hovering a pin highlights its row and the other way round, so a shopper can tell
    // which piece on the photo is which line. Class only — no layout, no DOM writes.
    look.querySelectorAll('[data-r69stl-pin]').forEach((pin) => {
      const n = pin.dataset.r69stlPin;
      const row = look.querySelector(`[data-r69stl-piece][data-n="${n}"]`);
      if (!row) return;
      const on = () => { pin.classList.add('r69stl-lit'); row.classList.add('r69stl-lit'); };
      const off = () => { pin.classList.remove('r69stl-lit'); row.classList.remove('r69stl-lit'); };
      pin.addEventListener('mouseenter', on);
      pin.addEventListener('mouseleave', off);
      row.addEventListener('mouseenter', on);
      row.addEventListener('mouseleave', off);
    });

    look._pieces.forEach((piece) => this.#syncPiece(piece));
    this.#recalc(look);
  }

  #wireTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.#activate(tab.dataset.r69stlTab));
      const warm = () => this.#warm(tab.dataset.r69stlTab);
      tab.addEventListener('pointerenter', warm, { once: true });
      tab.addEventListener('focus', warm, { once: true });
    });
    if (!this.tabrail) return;
    this.tabrail.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      const i = this.tabs.indexOf(document.activeElement);
      if (i < 0) return;
      e.preventDefault();
      const next = this.tabs[(i + (e.key === 'ArrowRight' ? 1 : this.tabs.length - 1)) % this.tabs.length];
      next.focus();
      this.#activate(next.dataset.r69stlTab);
    });
  }

  /*
   * A look sitting at display:none never downloads its photo, which is the point — nobody
   * pays for looks they don't open. But it also means the switch would show an empty frame
   * for as long as the fetch takes. Warming on tab hover/focus buys the picture on the first
   * sign of intent, so the switch is instant and an untouched tab still costs nothing.
   */
  #warm(key) {
    const look = this.looks.find((l) => l.dataset.r69stlLook === key);
    if (!look || look._warm) return;
    look._warm = true;
    look.querySelectorAll('img[src]').forEach((im) => {
      const pre = new Image();
      if (im.sizes) pre.sizes = im.sizes;
      if (im.srcset) pre.srcset = im.srcset;
      pre.src = im.src;
    });
  }

  #activate(key) {
    this.looks.forEach((l) => l.classList.toggle('r69stl-current', l.dataset.r69stlLook === key));
    this.tabs.forEach((t) => {
      const on = t.dataset.r69stlTab === key;
      t.classList.toggle('r69stl-current', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
  }

  /* ---------- Pieces ---------- */

  #variantOf(piece) {
    const vars = piece._m.vars;
    if (!piece._sel) return vars[0];
    return vars.find((v) => v.o === piece._sel.value);
  }

  #addable(piece) {
    const v = this.#variantOf(piece);
    return !!(v && v.a && !this.cartSet.has(String(v.id)));
  }

  #syncPiece(piece) {
    const v = this.#variantOf(piece);
    const add = piece.querySelector('[data-r69stl-add]');
    const bag = piece.querySelector('[data-r69stl-inbag]');
    const sold = piece.querySelector('[data-r69stl-soldout]');
    const img = piece.querySelector('[data-r69stl-thumb]');
    const price = piece.querySelector('[data-r69stl-price]');

    if (!v) {
      if (add) add.hidden = true;
      if (bag) bag.hidden = true;
      if (sold) sold.hidden = false;
      return;
    }
    if (img && v.img && img.getAttribute('src') !== v.img) {
      img.src = v.img;
      img.removeAttribute('srcset');
    }
    if (price) {
      const now = formatMoney(v.p, this.moneyFormat, this.currency);
      const html = v.c > v.p
        ? `<b class="r69stl__now">${esc(now)}</b> <s>${esc(formatMoney(v.c, this.moneyFormat, this.currency))}</s>`
        : `<b>${esc(now)}</b>`;
      if (price.innerHTML !== html) price.innerHTML = html;
    }

    const inBag = this.cartSet.has(String(v.id));
    if (sold) sold.hidden = v.a;
    if (bag) bag.hidden = !inBag;
    if (add) add.hidden = inBag || !v.a;
    piece.classList.toggle('r69stl-gone', !v.a);
  }

  #recalc(look) {
    const addable = look._pieces.filter((p) => this.#addable(p));
    const btn = look.querySelector('[data-r69stl-addall]');
    const label = look.querySelector('[data-r69stl-addlabel]');
    const done = look.querySelector('[data-r69stl-alllabel]');
    const total = look.querySelector('[data-r69stl-total]');
    if (!btn) return;

    // "Everything is already in the bag" is a pre-rendered sibling node, not a rewritten
    // string: swapping text nodes is what triggers the document-wide :has() invalidation.
    const empty = addable.length === 0;
    btn.disabled = empty;
    if (label) label.hidden = empty;
    if (done) done.hidden = !empty;
    if (!empty && total) {
      const sum = addable.reduce((s, p) => s + this.#variantOf(p).p, 0);
      const txt = formatMoney(sum, this.moneyFormat, this.currency);
      if (total.textContent !== txt) total.textContent = txt;
    }
  }

  #syncAll() {
    this.looks.forEach((look) => {
      look._pieces.forEach((piece) => this.#syncPiece(piece));
      this.#recalc(look);
    });
  }

  /* ---------- Cart ---------- */

  async #onCartEvent(e) {
    if (e && e.promise) { try { await e.promise; } catch (err) { /* the add failed; the refetch below still tells the truth */ } }
    this.#refreshCart();
  }

  async #fetchCart() {
    const res = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async #refreshCart() {
    try {
      const cart = await this.#fetchCart();
      this.#applyCart(cart);
    } catch (e) {
      /* offline or a cart route change — the section stays on the state it last knew */
    }
  }

  #applyCart(cart) {
    this.cartSet = new Set((cart.items || []).map((i) => String(i.variant_id)));
    this.#syncAll();
  }

  async #cartAdd(items, btn) {
    if (btn) btn.classList.add('r69stl-busy');

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const totalQuantity = items.reduce((s, it) => s + it.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'section',
      lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
      promise: deferred.promise,
    }));

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items.map((it) => ({ id: Number(it.id), quantity: it.quantity })),
          sections: sectionIds.join(','),
        }),
      }).then((r) => r.json());

      if (btn) btn.classList.remove('r69stl-busy');
      if (response && response.status) {
        deferred.reject(new Error(response.message || 'Add to cart failed'));
        this.#refreshCart();
        return;
      }
      if (btn) { btn.classList.add('r69stl-done'); setTimeout(() => btn.classList.remove('r69stl-done'), 1600); }

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'r69-shop-the-look',
          sourceId: this.id.toString(),
          itemCount: totalQuantity,
          sections: response.sections,
          didError: false,
        },
      });
      this.#applyCart(cart);
    } catch (e) {
      if (btn) btn.classList.remove('r69stl-busy');
      deferred.reject(e);
      this.#refreshCart();
    }
  }
}

if (!customElements.get('r69-shop-the-look')) {
  customElements.define('r69-shop-the-look', R69ShopTheLook);
}
